import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  TemplateBinding,
  listBindings,
  createBinding,
  updateBinding,
  deleteBinding,
} from '../services/TemplatesService';
import axiosInstance from '../../../config/axios';
import { EMPLOYMENT_TYPE_OPTIONS } from '../../employees/utils/constants';

interface TemplateBindingsPanelProps {
  templateId: number;
}

interface WorkArea {
  id: number;
  name: string;
}

interface Position {
  position_key: string;
  label: string;
  work_area: number;
}

export function TemplateBindingsPanel({ templateId }: TemplateBindingsPanelProps) {
  const [bindings, setBindings] = useState<TemplateBinding[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(false);

  const [formWorkArea, setFormWorkArea] = useState<number | null>(null);
  const [formPosition, setFormPosition] = useState<string>('');
  const [formEmploymentType, setFormEmploymentType] = useState<string>('');
  const [formPriority, setFormPriority] = useState<number>(100);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e0e3ef',
    borderRadius: 6,
    fontSize: 14,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 500,
    color: '#666',
  };

  useEffect(() => {
    loadBindings();
    loadWorkAreas();
  }, [templateId]);

  useEffect(() => {
    if (formWorkArea) {
      loadPositions(formWorkArea);
    } else {
      setPositions([]);
      setFormPosition('');
    }
  }, [formWorkArea]);

  const loadBindings = async () => {
    setLoading(true);
    try {
      const data = await listBindings(templateId);
      setBindings(data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setBindings([]);
      } else {
        console.error('Failed to load bindings:', err);
        const traceId = err?.response?.headers?.['x-trace-id'];
        toast.error(`Failed to load bindings${traceId ? ` (Trace: ${traceId})` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadWorkAreas = async () => {
    try {
      const { data } = await axiosInstance.get<WorkArea[]>('/api/admin/work-areas/');
      setWorkAreas(data.filter(wa => (wa as any).is_active !== false));
    } catch (err) {
      console.error('Failed to load work areas:', err);
    }
  };

  const loadPositions = async (waId: number) => {
    setLoadingPositions(true);
    try {
      const { data } = await axiosInstance.get<Position[]>(
        `/api/admin/work-areas/${waId}/positions/`
      );
      setPositions(data);
    } catch (err) {
      console.error('Failed to load positions:', err);
    } finally {
      setLoadingPositions(false);
    }
  };

  const resetForm = () => {
    setFormWorkArea(null);
    setFormPosition('');
    setFormEmploymentType('');
    setFormPriority(100);
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleAdd = async () => {
    try {
      await createBinding(templateId, {
        work_area: formWorkArea || undefined,
        position_key: formPosition || undefined,
        employment_type: formEmploymentType || undefined,
        priority: formPriority,
      });
      toast.success('Binding added successfully');
      loadBindings();
      resetForm();
    } catch (err: any) {
      console.error('Failed to add binding:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(`Failed to add binding${traceId ? ` (Trace: ${traceId})` : ''}`);
    }
  };

  const handleEdit = (binding: TemplateBinding) => {
    setEditingId(binding.id);
    setFormWorkArea(binding.work_area || null);
    setFormPosition(binding.position_key || '');
    setFormEmploymentType(binding.employment_type || '');
    setFormPriority(binding.priority);
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateBinding(templateId, editingId, {
        work_area: formWorkArea || undefined,
        position_key: formPosition || undefined,
        employment_type: formEmploymentType || undefined,
        priority: formPriority,
      });
      toast.success('Binding updated successfully');
      loadBindings();
      resetForm();
    } catch (err: any) {
      console.error('Failed to update binding:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(`Failed to update binding${traceId ? ` (Trace: ${traceId})` : ''}`);
    }
  };

  const handleDelete = async (bindingId: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this binding?');
    if (!confirmed) return;

    try {
      await deleteBinding(templateId, bindingId);
      toast.success('Binding deleted successfully');
      loadBindings();
    } catch (err: any) {
      console.error('Failed to delete binding:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(`Failed to delete binding${traceId ? ` (Trace: ${traceId})` : ''}`);
    }
  };

  const filteredPositions = formWorkArea
    ? positions.filter((p) => p.work_area === formWorkArea)
    : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f2b5a' }}>
          Template Bindings
        </h4>
        {!showAddForm && !editingId && (
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              padding: '6px 12px',
              background: '#021a4a',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <FaPlus size={10} />
            Add Binding
          </button>
        )}
      </div>

      <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
        Bindings control which templates are suggested in the Contract Wizard based on Work Area,
        Position, and Employment Type. Higher priority bindings appear first.
      </div>

      {(showAddForm || editingId) && (
        <div
          style={{
            padding: 16,
            border: '1px solid #e0e3ef',
            borderRadius: 6,
            marginBottom: 16,
            backgroundColor: '#f8f9fc',
          }}
        >
          <h5 style={{ margin: '0 0 16px 0', fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
            {editingId ? 'Edit Binding' : 'Add New Binding'}
          </h5>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Work Area</label>
              <select
                value={formWorkArea || ''}
                onChange={(e) => setFormWorkArea(e.target.value ? Number(e.target.value) : null)}
                style={inputStyle}
              >
                <option value="">Any Work Area</option>
                {workAreas.map((wa) => (
                  <option key={wa.id} value={wa.id}>
                    {wa.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Position</label>
              {loadingPositions ? (
                <div style={{ padding: '8px 12px', color: '#666', fontSize: 13 }}>
                  Loading positions...
                </div>
              ) : (
                <select
                  value={formPosition}
                  onChange={(e) => setFormPosition(e.target.value)}
                  disabled={!formWorkArea}
                  style={inputStyle}
                >
                  <option value="">Any Position</option>
                  {filteredPositions.map((pos) => (
                    <option key={pos.position_key} value={pos.position_key}>
                      {pos.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Employment Type</label>
              <select
                value={formEmploymentType}
                onChange={(e) => setFormEmploymentType(e.target.value)}
                style={inputStyle}
              >
                <option value="">Any Type</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Priority</label>
              <input
                type="number"
                value={formPriority}
                onChange={(e) => setFormPriority(Number(e.target.value))}
                style={inputStyle}
                placeholder="100"
              />
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                Higher priority = shown first
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={resetForm}
              style={{
                padding: '8px 16px',
                background: '#f5f5f5',
                border: '1px solid #e0e3ef',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                color: '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaTimes size={12} />
              Cancel
            </button>
            <button
              onClick={editingId ? handleUpdate : handleAdd}
              style={{
                padding: '8px 16px',
                background: '#021a4a',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaSave size={12} />
              {editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
          Loading bindings...
        </div>
      ) : bindings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 13 }}>
          No bindings configured. This template will not be auto-suggested in the wizard.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bindings.map((binding) => (
            <div
              key={binding.id}
              style={{
                padding: 16,
                border: '1px solid #e0e3ef',
                borderRadius: 6,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                  {binding.work_area_name && (
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                      }}
                    >
                      WA: {binding.work_area_name}
                    </span>
                  )}
                  {binding.position_label && (
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                      }}
                    >
                      Pos: {binding.position_label}
                    </span>
                  )}
                  {binding.employment_type && (
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: '#fff3e0',
                        color: '#f57c00',
                      }}
                    >
                      Type: {binding.employment_type}
                    </span>
                  )}
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: '#e8f5e9',
                      color: '#388e3c',
                    }}
                  >
                    Priority: {binding.priority}
                  </span>
                </div>
                {!binding.work_area_name && !binding.position_label && !binding.employment_type && (
                  <div style={{ fontSize: 13, color: '#666' }}>Default binding (matches all)</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleEdit(binding)}
                  disabled={showAddForm || editingId !== null}
                  style={{
                    padding: '6px 12px',
                    background: '#f5f5f5',
                    border: '1px solid #e0e3ef',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#021a4a',
                    cursor: showAddForm || editingId !== null ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <FaEdit size={10} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(binding.id)}
                  disabled={showAddForm || editingId !== null}
                  style={{
                    padding: '6px 12px',
                    background: '#ffebee',
                    border: '1px solid #d32f2f',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#d32f2f',
                    cursor: showAddForm || editingId !== null ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <FaTrash size={10} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
